import axios, { AxiosHeaders, InternalAxiosRequestConfig } from "axios";
import { getAccessToken } from "../../utils/amplifyAuth";
import { store } from "../../store";
import {
  decrementLoading,
  incrementLoading,
  setError,
  setSuccess,
} from "../../features/msp/slices/apiStatusSlice";
import { logout } from "../../services/authService";

const BASE_URL = import.meta.env.VITE_API_URL;

export interface CustomAxiosRequestConfig extends InternalAxiosRequestConfig {
  /** Skip global loading overlay for silent/fast calls */
  skipLoadingOverlay?: boolean;
  /** If true, suppress success toast even for non-GET */
  suppressSuccessToast?: boolean;
}

const api = axios.create({
  baseURL: BASE_URL,
  timeout: 10000,
});

// defensive counter (keeps slice in sync even if an interceptor throws)
let activeRequests = 0;

const startRequest = () => {
  activeRequests++;
  store.dispatch(incrementLoading());
};

const endRequest = () => {
  activeRequests = Math.max(0, activeRequests - 1);
  store.dispatch(decrementLoading());
};

/** Public request helper (still goes through interceptors) */
export const makePublicRequest = (config: any) =>
  api.request({ ...config, timeout: config.timeout ?? 30000 });

/** Authenticated request helper (token is attached by request interceptor) */
export const makeAuthenticatedRequest = (config: any) =>
  api.request({ ...config, timeout: config.timeout ?? 30000 });

/* -------------------------
   Interceptors
-------------------------- */
api.interceptors.request.use(
  async (config: CustomAxiosRequestConfig) => {
    try {
      if (!config.skipLoadingOverlay) startRequest();

      // token + headers
      const token = await getAccessToken();
      config.headers = AxiosHeaders.from(config.headers || {});
      if (token) {
        (config.headers as AxiosHeaders).set(
          "Authorization",
          `Bearer ${token}`
        );
      }
      (config.headers as AxiosHeaders).set("Accept", "application/json");
      (config.headers as AxiosHeaders).set(
        "ngrok-skip-browser-warning",
        "true"
      );

      // debug
      console.debug("[api] ->", config.method?.toUpperCase(), config.url);
      return config;
    } catch (err) {
      console.error("[api] request interceptor error:", err);
      if (!config.skipLoadingOverlay) endRequest();
      return config;
    }
  },
  (error) => {
    console.error("[api] request creation error:", error);
    endRequest();
    return Promise.reject(error);
  }
);

api.interceptors.response.use(
  (response) => {
    const cfg = response.config as CustomAxiosRequestConfig;
    if (!cfg?.skipLoadingOverlay) endRequest();

    const method = (response.config?.method || "").toLowerCase();
    if (method !== "get" && !cfg?.suppressSuccessToast) {
      const msg = response.data?.message || "Request successful";
      store.dispatch(setSuccess(String(msg)));
    }
    return response;
  },
  (error) => {
    const cfg = (error?.config || {}) as CustomAxiosRequestConfig;
    if (!cfg?.skipLoadingOverlay) endRequest();

    const status = error?.response?.status;
    console.error("[api] error:", {
      url: error.config?.url,
      status,
      data: error.response?.data,
      message: error.message,
    });

    if (status === 401) {
      logout();
      return Promise.reject(error);
    }

    store.dispatch(
      setError(
        error?.response?.data?.message ||
          error.message ||
          "Something went wrong. Please try again."
      )
    );
    return Promise.reject(error);
  }
);

export default api;
